import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

const faqs = [
  {
    question: 'How does scan-and-pay shopping work?',
    answer: 'It\'s simple! Open the GrabPe app when you enter a partner store, scan the barcode of each product you want to buy using your phone camera, and it gets added to your digital cart. When you\'re done shopping, pay securely in the app and show your digital receipt at the exit. No queues, no waiting!',
  },
  {
    question: 'Is GrabPe free for shoppers?',
    answer: 'Yes, GrabPe is completely free for shoppers! Download the app, create an account, and start shopping smarter. There are no subscription fees, no hidden charges. Stores pay a small transaction fee to offer this convenience to their customers.',
  },
  {
    question: 'What if I scan an item by mistake?',
    answer: 'No problem! You can easily remove any item from your cart before you pay. Just open your cart, swipe left on the item, and tap delete. You can also adjust quantities if needed. Review your cart as many times as you want before checkout.',
  },
  {
    question: 'How do stores prevent theft?',
    answer: 'We have multiple security measures in place. Store staff can verify digital receipts at exit points. Our AI system flags suspicious patterns. Random verification checks may occur. Partner stores also have existing security systems that work alongside GrabPe.',
  },
  {
    question: 'Which stores accept GrabPe?',
    answer: 'We\'re partnered with over 2,500 stores including supermarkets, convenience stores, pharmacies, and retail chains. Open the app to see GrabPe-enabled stores near you. New stores are added weekly as we expand across the country.',
  },
  {
    question: 'What payment methods are supported?',
    answer: 'GrabPe supports all major payment methods including credit/debit cards (Visa, Mastercard, Amex), digital wallets (Apple Pay, Google Pay), and bank transfers. You can save multiple payment methods and choose at checkout.',
  },
  {
    question: 'How do I integrate GrabPe in my store?',
    answer: 'Getting started is easy! Sign up for a store account, and our team will help you integrate GrabPe with your existing POS and inventory system. Most integrations are completed within 1-2 weeks. We provide training materials and dedicated support throughout.',
  },
  {
    question: 'What kind of support do you offer?',
    answer: 'Shoppers get 24/7 in-app chat support and a comprehensive FAQ. Store owners on paid plans get priority email and phone support. Enterprise customers receive a dedicated account manager, on-site training, and guaranteed response times via SLA.',
  },
];

const FAQ = () => {
  return (
    <section id="faq" className="py-20 md:py-32 bg-white">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-semibold tracking-wide uppercase text-brand-primary mb-4 block">
            FAQ
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-brand-secondary mb-6">
            Got{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-primary to-brand-accent">
              questions?
            </span>
          </h2>
          <p className="text-lg md:text-xl font-medium leading-relaxed text-neutral-text-secondary">
            Everything you need to know about GrabPe
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-neutral-bg-secondary rounded-2xl border-none px-6 data-[state=open]:shadow-lg transition-shadow"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-heading font-bold text-brand-secondary hover:text-brand-primary hover:no-underline py-6 text-lg">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-neutral-text-secondary leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-16 p-8 bg-neutral-bg-secondary rounded-2xl"
        >
          <h3 className="font-heading text-xl font-bold text-brand-secondary mb-2">
            Still have questions?
          </h3>
          <p className="text-neutral-text-secondary mb-6">
            Can't find the answer you're looking for? Our team is here to help.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full px-8 py-4 font-bold shadow-[0_4px_14px_0_rgba(99,91,255,0.39)] transition-all hover:scale-105 active:scale-95"
            data-testid="faq-contact-btn"
          >
            Contact support
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
